import { Link, Typography, useTheme, List, Box, Stack } from '@mui/material';
import { useGetNotificationListQuery, useUpdateNotificationMutation } from 'api/notification';
import NotificationItem from './NotificationItem';
import NotificationSkeletons from './NotificationSkeletons';
import { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NotificationTypes, NotificationUpdatePayloadTypes } from 'types/notification';
import ScrollX from 'components/ScrollX';

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

interface Props {
  notificationCount: number;
}

const NotificationList: FC<Props> = ({ notificationCount }) => {
  const theme = useTheme();
  const [isFiltered, setIsFiltered] = useState<boolean>(true);
  const { data: notificaitonList, isLoading: isNotificationListLoading } = useGetNotificationListQuery();
  const [updateNotificationQuery] = useUpdateNotificationMutation();

  const onClickNotification = async (notificationId: number, isMarkAllRead: boolean = false) => {
    try {
      const payload: NotificationUpdatePayloadTypes = { notificationId, status: 'Read', isMarkAllRead };
      await updateNotificationQuery(payload).unwrap();
    } catch (e) {
      console.log(e);
    }
  };

  const filteredNotificationList: NotificationTypes[] = useMemo(
    () => (isFiltered ? notificaitonList?.slice(0, 10) || [] : notificaitonList || []),
    [isFiltered, notificaitonList]
  );

  return (
    <Box p={3} pr={0}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" pr={3}>
        <Typography variant="h5">
          <FormattedMessage id="components.notifications.title" />
        </Typography>
        {notificationCount > 0 && (
          <Link href="#" variant="h6" color="primary" onClick={() => onClickNotification(0, true)}>
            <FormattedMessage id="components.notifications.markAllAsRead" />
          </Link>
        )}
      </Stack>
      {/* Notification List Start */}
      <ScrollX sx={{ maxHeight: '70vh', overflow: 'auto', pr: 1.7 }}>
        <List
          component="nav"
          sx={{
            '& .MuiListItemButton-root': {
              p: 1.5,
              mb: 1.5,
              mt: 0,
              pt: 0,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                bgcolor: 'primary.100',
                borderColor: theme.palette.primary.light
              },
              '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
            }
          }}
        >
          {isNotificationListLoading ? (
            Array(notificationCount)
              .fill(1)
              .map(() => <NotificationSkeletons />)
          ) : filteredNotificationList.length > 0 ? (
            filteredNotificationList.map((notification) => (
              <NotificationItem key={notification.notificationID} data={notification} onClickNotification={onClickNotification} />
            ))
          ) : (
            <Typography textAlign="center" mt={1}>
              No records found
            </Typography>
          )}
          {isFiltered && filteredNotificationList.length < (notificaitonList || []).length && (
            <Stack direction="row" justifyContent="center">
              <Link href="#" variant="h6" color="primary" onClick={() => setIsFiltered(false)}>
                <FormattedMessage id="components.notifications.viewAll" />
              </Link>
            </Stack>
          )}
        </List>
      </ScrollX>

      {/* Notification List End */}
    </Box>
  );
};

export default NotificationList;
